import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import Heading from '@/components/Typography/Heading';
import AppLayout from '@/layouts/AppLayout';
import { usePage } from '@inertiajs/react';
import React from 'react';

interface ToolEmbed {
    heading?: string;
    src: string;
    aspect_ratio?: string;
    width?: number;
    height?: number;
    allow_fullscreen?: boolean;
}

interface ToolPageConfig {
    title: string;
    intro?: string;
    paragraphs?: string[];
    embeds: ToolEmbed[];
}

interface PageProps {
    static_page: {
        id: number;
        name: string;
        slug: string;
        config: ToolPageConfig;
    };
}

const EmbedBlock: React.FC<{ embed: ToolEmbed; title: string }> = ({
    embed,
    title,
}) => {
    const isResponsive = embed.aspect_ratio === '16:9';

    return (
        <div className="space-y-3">
            {embed.heading && (
                <Heading className="text-3xl">{embed.heading}</Heading>
            )}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                {isResponsive ? (
                    <div className="relative" style={{ paddingTop: '56.25%' }}>
                        <iframe
                            src={embed.src}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full"
                            style={{ border: 0 }}
                            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                            allowFullScreen={embed.allow_fullscreen ?? true}
                            title={embed.heading ?? title}
                        />
                    </div>
                ) : (
                    <div className="w-full">
                        <iframe
                            src={embed.src}
                            loading="lazy"
                            width="100%"
                            height={embed.height}
                            style={{ border: 0, display: 'block' }}
                            allowFullScreen={embed.allow_fullscreen ?? false}
                            title={embed.heading ?? title}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const ToolPageContent: React.FC = () => {
    const { static_page } = usePage<{ static_page: PageProps['static_page'] }>()
        .props;
    const config = static_page.config;

    return (
        <AppLayout>
            <PageTitle title={static_page.name} />
            <div className="space-y-6">
                <Heading as="h1" className="text-4xl sm:text-5xl">
                    {config.title}
                </Heading>

                {config.intro && (
                    <p className="text-lg text-gray-700">{config.intro}</p>
                )}

                {config.paragraphs?.map((paragraph, i) => (
                    <p key={i} className="text-lg text-gray-700">
                        {paragraph}
                    </p>
                ))}

                {config.embeds.map((embed, i) => (
                    <EmbedBlock key={i} embed={embed} title={config.title} />
                ))}
            </div>
        </AppLayout>
    );
};

const ToolPage: React.FC = () => (
    <ThemeWrapper>
        <ToolPageContent />
    </ThemeWrapper>
);

export default ToolPage;
